import { Video, Users, Presentation, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { scrollToId } from "@/hooks/useLenis";

const CARDS = [
  {
    icon: Video,
    badge: "Self-Paced",
    title: "Recorded Courses",
    desc: "Comprehensive video modules on reference managers, academic writing, and data analysis. Buy once and study on your own timeline.",
    action: "Browse Courses",
    target: "courses",
    span: "md:col-span-7",
    dark: false,
  },
  {
    icon: Users,
    badge: "Cohort-Based",
    title: "Live Interactive Classes",
    desc: "Small-group live sessions with Q&A, real-time citation troubleshooting, and personal feedback on your work.",
    action: "View Live Sessions",
    target: "live",
    span: "md:col-span-5",
    dark: false,
  },
  {
    icon: Presentation,
    badge: "Featured Event",
    title: "Hands-on Workshops",
    desc: "Intensive live workshops — starting with our 2-Day Google Meet workshop on the Mendeley Reference Manager. Limited seats, certificate included.",
    action: "Register via Google Form",
    target: "form",
    span: "md:col-span-12",
    dark: true,
  },
];

export default function Offerings({ content }) {
  const formUrl = content?.workshop_form_url;

  const Action = ({ card, index }) => {
    const cls = `group/btn mt-8 sm:mt-0 self-start sm:self-auto inline-flex items-center gap-2 text-sm font-medium rounded-full px-6 py-3 transition-colors duration-300 ${
      card.dark
        ? "bg-cherry hover:bg-[#B81919] text-white"
        : "border border-navy/20 text-navy hover:bg-navy hover:text-white"
    }`;
    const icon = <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />;
    if (card.target === "form" && formUrl) {
      return (
        <a href={formUrl} target="_blank" rel="noopener noreferrer" data-testid={`offering-action-${index}`} className={cls}>
          {card.action} {icon}
        </a>
      );
    }
    return (
      <button data-testid={`offering-action-${index}`} onClick={() => scrollToId(card.target === "form" ? "live" : card.target)} className={cls}>
        {card.action} {icon}
      </button>
    );
  };

  return (
    <section id="offerings" data-testid="offerings-section" className="bg-white py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <Overline>What We Offer</Overline>
          <h2 className="mt-6 font-serif font-bold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]" data-testid="offerings-title">
            Learning formats engineered for researchers
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {CARDS.map((card, i) => (
            <Reveal key={card.title} delay={0.1 + i * 0.12} className={card.span}>
              <div
                data-testid={`offering-card-${i}`}
                className={`group relative h-full rounded-2xl p-8 sm:p-10 border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                  card.dark
                    ? "bg-navy border-navy text-white hover:shadow-navy/30"
                    : "bg-chalk border-navy/10 hover:border-cherry/30"
                }`}
              >
                <div className={`flex ${card.dark ? "flex-col sm:flex-row sm:items-center sm:justify-between gap-8" : "flex-col"} h-full`}>
                  <div className={card.dark ? "max-w-xl" : ""}>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${card.dark ? "bg-cherry text-white" : "bg-navy text-white"}`}>
                        <card.icon className="h-5 w-5" />
                      </span>
                      <span className={`text-[11px] font-mono uppercase tracking-[0.25em] ${card.dark ? "text-sky" : "text-cherry"}`}>
                        {card.badge}
                      </span>
                    </div>
                    <h3 className={`mt-6 font-serif text-2xl sm:text-3xl font-semibold ${card.dark ? "text-white" : "text-navy"}`}>
                      {card.title}
                    </h3>
                    <p className={`mt-4 text-sm sm:text-base font-sans leading-relaxed ${card.dark ? "text-white/70" : "text-[#5B6B7C]"}`}>
                      {card.desc}
                    </p>
                  </div>
                  <Action card={card} index={i} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
